/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import type { CopyRequest, CopySent, DefaultCopy } from '@/api/agreements';
import { MAX_COPY_BODY, MAX_COPY_RECIPIENTS, MAX_COPY_SUBJECT } from '@/api/packets';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toast';
import { LOOKS_LIKE_EMAIL, NO_AUTOFILL } from '@/lib/agreementFields';
import { ApiError } from '@/lib/queryClient';

/** Split what was typed or pasted into addresses: commas, semicolons, spaces. */
function splitAddresses(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * A list of email addresses, as chips. Typed, then Enter, a comma or leaving
 * the field adds it; a pasted list is split. Each is checked for shape here,
 * and again by the server.
 */
export function AddressList({
  id,
  value,
  onChange,
  max = MAX_COPY_RECIPIENTS,
}: {
  id: string;
  value: readonly string[];
  onChange: (next: string[]) => void;
  max?: number;
}) {
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const known = new Set(value.map((address) => address.toLowerCase()));
    const next = [...value];
    for (const address of splitAddresses(raw)) {
      if (next.length >= max) break;
      if (known.has(address.toLowerCase())) continue;
      known.add(address.toLowerCase());
      next.push(address);
    }
    onChange(next);
    setDraft('');
  };

  const bad = value.filter((address) => !LOOKS_LIKE_EMAIL.test(address));

  return (
    <>
      <div className="addr-list">
        {value.map((address) => (
          <span
            key={address}
            className={`addr-chip${LOOKS_LIKE_EMAIL.test(address) ? '' : ' bad'}`}
          >
            {address}
            <button
              type="button"
              aria-label={`Remove ${address}`}
              onClick={() => {
                onChange(value.filter((a) => a !== address));
              }}
            >
              <Icon name="x" size={11} />
            </button>
          </span>
        ))}
        {value.length < max ? (
          <input
            id={id}
            className="addr-input"
            type="email"
            multiple
            {...NO_AUTOFILL}
            value={draft}
            placeholder={value.length === 0 ? 'name@company.com' : ''}
            onChange={(event) => {
              const typed = event.target.value;
              if (/[,;]/.test(typed)) add(typed);
              else setDraft(typed);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && draft.trim()) {
                event.preventDefault();
                add(draft);
              } else if (event.key === 'Backspace' && !draft && value.length > 0) {
                onChange(value.slice(0, -1));
              }
            }}
            onBlur={() => {
              if (draft.trim()) add(draft);
            }}
            onPaste={(event) => {
              const pasted = event.clipboardData.getData('text');
              if (/[\s,;]/.test(pasted.trim())) {
                event.preventDefault();
                add(pasted);
              }
            }}
          />
        ) : null}
      </div>
      {bad.length > 0 ? (
        <span className="err">
          {bad.length === 1
            ? `${bad[0] ?? ''} does not look like an email address.`
            : `${String(bad.length)} of these do not look like email addresses.`}
        </span>
      ) : (
        <span className="hint hint-below">
          Up to {max}. Each person gets their own email.
        </span>
      )}
    </>
  );
}

/**
 * Sending a copy of a filed agreement's signed PDF (`PKT-21`, `LIFE-70`,
 * ADR-0008): a packet's step, or any filed agreement. `send` says which.
 *
 * Prefilled from the default copy, and changed freely. The document leaves
 * Amida's store with the email, and the dialog says so before anything is
 * sent. A person presses Send; nothing sends a copy by itself.
 */
export function SendCopy({
  send: sendIt,
  title,
  defaultCopy,
  onClose,
}: {
  /** Posts the copy to the route for what is being sent. */
  send: (copy: CopyRequest) => Promise<CopySent>;
  title: string;
  defaultCopy: DefaultCopy | null;
  onClose: () => void;
}) {
  const [to, setTo] = useState<string[]>(defaultCopy?.to ?? []);
  const [subject, setSubject] = useState(defaultCopy?.subject ?? `${title} — signed`);
  const [body, setBody] = useState(defaultCopy?.body ?? '');

  const send = useMutation({
    mutationFn: () => sendIt({ to, subject: subject.trim(), body }),
    onSuccess: (result) => {
      onClose();
      toast(
        result.sent === 1
          ? 'Sent to 1 person.'
          : `Sent to ${String(result.sent)} people.`,
      );
    },
  });

  const ready =
    to.length > 0 &&
    to.every((address) => LOOKS_LIKE_EMAIL.test(address)) &&
    body.trim().length > 0;
  const failed = send.error instanceof ApiError ? send.error : undefined;

  return (
    <Modal
      title={`Send a copy of ${title}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn-p"
            disabled={!ready || send.isPending}
            onClick={() => {
              send.mutate();
            }}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
          <button type="button" className="btn btn-q" onClick={onClose}>
            Cancel
          </button>
        </>
      }
    >
      <div className="pane stack p-0! gap-lg!">
        <div className="field">
          <label htmlFor="copy-to">
            To <span className="req">*</span>
          </label>
          <AddressList id="copy-to" value={to} onChange={setTo} />
        </div>
        <div className="field">
          <label htmlFor="copy-subject">Subject</label>
          <input
            className="inp"
            id="copy-subject"
            {...NO_AUTOFILL}
            value={subject}
            maxLength={MAX_COPY_SUBJECT}
            onChange={(event) => {
              setSubject(event.target.value);
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="copy-body">
            Message <span className="req">*</span>
          </label>
          <textarea
            className="inp"
            id="copy-body"
            rows={5}
            value={body}
            maxLength={MAX_COPY_BODY}
            onChange={(event) => {
              setBody(event.target.value);
            }}
          />
        </div>
        <p className="foot-note p-0! m-0!">
          <Icon name="shield" size={13} />
          The signed PDF is attached and leaves Amida&apos;s store with this email.
        </p>
        {failed ? (
          <p className="warn-line" role="alert">
            <Icon name="warn" size={14} />
            {failed.detail || 'It could not be sent.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
